import { supabase } from '../lib/supabase';
import { User } from '../types/User';

export interface AuthUser extends User {
  id: string;
}

export class AuthService {
  // Test Supabase connection
  static async testConnection(): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('Testing Supabase connection...');
      
      const { data, error } = await supabase
        .from('users')
        .select('count')
        .limit(1);
      
      if (error) {
        console.error('Supabase connection test failed:', error);
        return { success: false, error: error.message };
      }
      
      console.log('Supabase connection test successful');
      return { success: true, error: null };
    } catch (error) {
      console.error('Supabase connection test error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown connection error' 
      };
    }
  }

  // Register new user
  static async register(userData: {
    email: string;
    password: string;
    name: string;
    company: string;
    position: string;
    country: string;
    about?: string;
    role?: 'user' | 'admin';
  }): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      console.log('Attempting to register user:', userData.email);
      
      // Test connection first
      const { data: testData, error: testError } = await supabase
        .from('users')
        .select('count')
        .limit(1);
      
      if (testError) {
        console.error('Database connection test failed:', testError);
        return { user: null, error: 'Database connection failed: ' + testError.message };
      }
      
      console.log('Database connection successful');
      
      // 1. First attempt to create new auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
      });

      // If signup fails because user already exists, try to sign in
      if (authError && authError.message.includes('User already registered')) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: userData.email,
          password: userData.password,
        });

        if (signInError) {
          if (signInError.message.includes('Invalid login credentials')) {
            return { user: null, error: 'User already exists with different password' };
          }
          return { user: null, error: signInError.message };
        }

        if (!signInData.user) {
          return { user: null, error: 'Failed to sign in existing user' };
        }

        // Get or create profile for existing user
        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', signInData.user.id)
          .maybeSingle();

        if (profileError || !profileData) {
          // Create profile if it doesn't exist
          const { data: newProfileData, error: newProfileError } = await supabase
            .from('users')
            .upsert({
              id: signInData.user.id,
              email: userData.email,
              name: userData.name,
              company: userData.company,
              position: userData.position,
              country: userData.country,
              about: userData.about || '',
            }, {
              onConflict: 'id'
            })
            .select()
            .single();

          if (newProfileError) {
            return { user: null, error: newProfileError.message };
          }

          const user: AuthUser = {
            id: newProfileData.id,
            name: newProfileData.name,
            email: newProfileData.email,
            company: newProfileData.company,
            position: newProfileData.position,
            country: newProfileData.country,
            about: newProfileData.about,
            avatar: newProfileData.avatar_url,
            role: newProfileData.email === 'admin@logistics.com' || newProfileData.role === 'admin' ? 'admin' : 'user',
          };

          return { user, error: null };
        }

        const user: AuthUser = {
          id: profileData.id,
          name: profileData.name,
          email: profileData.email,
          company: profileData.company,
          position: profileData.position,
          country: profileData.country,
          about: profileData.about,
          avatar: profileData.avatar_url,
          role: profileData.email === 'admin@logistics.com' || profileData.role === 'admin' ? 'admin' : 'user',
        };

        return { user, error: null };
      }

      // If other signup error occurred
      if (authError) {
        return { user: null, error: authError.message };
      }

      // New user created successfully
      if (!authData.user) {
        return { user: null, error: 'Failed to create user' };
      }

      // 2. Create profile for new user
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .upsert({
          id: authData.user.id,
          email: userData.email,
          name: userData.name,
          company: userData.company,
          position: userData.position,
          country: userData.country,
          about: userData.about || '',
        }, {
          onConflict: 'id'
        })
        .select()
        .single();

      if (profileError) {
        return { user: null, error: profileError.message };
      }

      const user: AuthUser = {
        id: profileData.id,
        name: profileData.name,
        email: profileData.email,
        company: profileData.company,
        position: profileData.position,
        country: profileData.country,
        about: profileData.about,
        avatar: profileData.avatar_url,
        role: profileData.role === 'admin' || profileData.email === 'admin@logistics.com' ? 'admin' : 'user',
      };

      return { user, error: null };
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        return { user: null, error: 'Network error: Unable to connect to server. Please check your internet connection and try again.' };
      }
      
      if (error instanceof Error) {
        const errorMessage = error.message;
        
        // Check for duplicate email constraint violation
        if (errorMessage.includes('duplicate key value violates unique constraint "users_email_key"') || 
            errorMessage.includes('23505')) {
          return { user: null, error: 'User already registered' };
        }
        
        // Check for network/CORS errors
        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          return { user: null, error: 'Network error: Unable to connect to server. Please check your internet connection and try again.' };
        }
        
        return { user: null, error: 'Registration failed: ' + errorMessage };
      }
      
      return { user: null, error: 'Registration failed: Unknown error occurred' };
    }
  }

  // Login user
  static async login(email: string, password: string): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        // Provide more user-friendly error messages
        let errorMessage = authError.message;
        if (authError.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid login credentials. Please check your email and password.';
        } else if (authError.message.includes('Email not confirmed')) {
          errorMessage = 'Email adresa nije potvrđena. Proverite inbox.';
        }
        return { user: null, error: errorMessage };
      }

      if (!authData.user) {
        return { user: null, error: 'Login failed' };
      }

      // Get user profile
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError || !profileData) {
        return { user: null, error: 'Failed to load profile' };
      }

      const user: AuthUser = {
        id: profileData.id,
        name: profileData.name,
        email: profileData.email,
        company: profileData.company,
        position: profileData.position,
        country: profileData.country,
        about: profileData.about,
        avatar: profileData.avatar_url,
        role: profileData.role === 'admin' || profileData.email === 'admin@logistics.com' ? 'admin' : 'user',
      };

      return { user, error: null };
    } catch (error) {
      return { user: null, error: 'Login failed' };
    }
  }

  // Logout user
  static async logout(): Promise<{ error: string | null }> {
    const { error } = await supabase.auth.signOut();
    return { error: error?.message || null };
  }

  // Get current user
  static async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) return null;

      const { data: profileData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (error || !profileData) return null;

      return {
        id: profileData.id,
        name: profileData.name,
        email: profileData.email,
        company: profileData.company,
        position: profileData.position,
        country: profileData.country,
        about: profileData.about,
        avatar: profileData.avatar_url,
        role: profileData.role === 'admin' || profileData.email === 'admin@logistics.com' ? 'admin' : 'user',
      };
    } catch (error) {
      return null;
    }
  }

  // Request password reset
  static async requestPasswordReset(email: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to send reset email' };
    }
  }

  static async updatePassword(password: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      return { error: 'Failed to update password' };
    }
  }

  // Update user profile
  static async updateProfile(userId: string, updates: {
    name?: string;
    company?: string;
    position?: string;
    country?: string;
    about?: string;
    avatar_url?: string;
  }): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error || !data) {
        return { user: null, error: error.message };
      }

      const user: AuthUser = {
        id: data.id,
        name: data.name,
        email: data.email,
        company: data.company,
        position: data.position,
        country: data.country,
        about: data.about,
        avatar: data.avatar_url,
        role: data.role === 'admin' || data.email === 'admin@logistics.com' ? 'admin' : 'user',
      };

      return { user, error: null };
    } catch (error) {
      return { user: null, error: 'Failed to update profile' };
    }
  }

  // Convert image file to base64 for storage
  static async convertImageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}